import { Subject } from "rxjs";
import { debounceTime, filter } from "rxjs/operators";

export class ScrollSpySingleton {
  static instance;

  // Scan band: sections are "active candidates" while they overlap the zone
  // between BAND_TOP px from the viewport top and BAND_BOTTOM_RATIO of the
  // viewport height. Threshold-based ratios fail when section heights vary
  // (a section taller than ~2x the viewport can never reach a 0.5 ratio),
  // so intersection only gates candidacy — the active section is resolved
  // geometrically in #resolveActiveSection.
  static BAND_TOP = 160;
  static BAND_BOTTOM_RATIO = 0.45;

  constructor() {
    if (ScrollSpySingleton.instance) {
      return ScrollSpySingleton.instance;
    }

    this._candidates = new Set();
    this._activeEl = undefined;

    this._entries$ = new Subject();
    this._intersections$ = this._entries$.pipe(
      debounceTime(100),
      filter((entriesArray) => entriesArray.length === 1),
    );

    this.observer = this.createObserver();
    ScrollSpySingleton.instance = this;
  }

  createObserver() {
    const bandBottomMargin = Math.round(
      (1 - ScrollSpySingleton.BAND_BOTTOM_RATIO) * 100,
    );

    const observerOptions = {
      // The window scrolls this app (#stage-view is a non-scrolling wrapper),
      // so the root is the viewport, shrunk to the scan band.
      root: null,
      rootMargin: `-${ScrollSpySingleton.BAND_TOP}px 0px -${bandBottomMargin}% 0px`,
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this._candidates.add(entry.target);
        } else {
          this._candidates.delete(entry.target);
        }
      });

      this.#resolveActiveSection();
    }, observerOptions);

    return observer;
  }

  // Active section = the candidate whose top edge sits lowest while still
  // inside the band — i.e. the section most recently scrolled into it.
  // Tall sections stay active while they span the band; a sibling takes
  // over the moment its own top crosses in.
  #resolveActiveSection() {
    let activeEl;
    let maxTop = -Infinity;

    this._candidates.forEach((el) => {
      if (el.isConnected === false) {
        this._candidates.delete(el);
        return;
      }
      const { top } = el.getBoundingClientRect();
      if (top > maxTop) {
        maxTop = top;
        activeEl = el;
      }
    });

    if (activeEl !== undefined && activeEl !== this._activeEl) {
      this._activeEl = activeEl;
      // Emitted in the same [{target}] shape subscribers already consume.
      this._entries$.next([{ target: activeEl }]);
    }
  }

  // Re-emits the current active section. Called when a pause lifts:
  // emissions swallowed while the spy was disabled are otherwise lost,
  // leaving the route stale after a programmatic scroll completes.
  emitActive() {
    if (this._activeEl !== undefined && this._activeEl.isConnected) {
      this._entries$.next([{ target: this._activeEl }]);
    }
  }

  addSectionObserver(sectionNode) {
    this.observer.observe(sectionNode);
  }

  removeSectionObserver(sectionNode) {
    this.observer.unobserve(sectionNode);
    this._candidates.delete(sectionNode);
    if (this._activeEl === sectionNode) {
      this._activeEl = undefined;
    }
  }

  // Getter method to access the intersections$ observable
  get intersections$() {
    return this._intersections$;
  }
}
